import { formatValue, humanizeMeasure, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";

export function buildSankeyOption({ viz, query, data, palette, base, measure }: ChartBuildCtx): ChartOption {
  const dims = query.dimensions ?? [];
  const srcKey = viz.encodings.x ?? dims[0] ?? "source";
  const tgtKey = viz.encodings.y ?? dims[1] ?? "target";
  const valueKey = measure;
  const valueFmt = formatFromColumn(valueKey, data.columns);
  const rawLinks = data.rows.map((r) => {
    const s = String(r[srcKey] ?? "");
    const t = String(r[tgtKey] ?? "");
    return { source: s, target: t, value: Number(r[valueKey] ?? 0) };
  }).filter((l) => l.value > 0 && l.source && l.target);

  // Roll up small flows. Per-source: any target whose flow is < 2%
  // of that source's total gets merged into a "Other ← <source>"
  // bucket. Per-target gets a mirror "Other → <target>" bucket.
  // Keeps the leading paths legible without losing the long tail.
  const SHARE_THRESHOLD = 0.02;
  const totalsBySource = new Map<string, number>();
  const totalsByTarget = new Map<string, number>();
  for (const l of rawLinks) {
    totalsBySource.set(l.source, (totalsBySource.get(l.source) ?? 0) + l.value);
    totalsByTarget.set(l.target, (totalsByTarget.get(l.target) ?? 0) + l.value);
  }
  const links: typeof rawLinks = [];
  const otherAccBySource = new Map<string, number>();
  for (const l of rawLinks) {
    const srcTotal = totalsBySource.get(l.source) ?? 0;
    if (srcTotal > 0 && l.value / srcTotal < SHARE_THRESHOLD) {
      otherAccBySource.set(l.source, (otherAccBySource.get(l.source) ?? 0) + l.value);
    } else {
      links.push(l);
    }
  }
  for (const [src, val] of otherAccBySource) {
    if (val > 0) links.push({ source: src, target: "Other", value: val });
  }
  const nodeSet = new Set<string>();
  for (const l of links) {
    nodeSet.add(l.source);
    nodeSet.add(l.target);
  }
  return {
    ...base,
    tooltip: {
      trigger: "item",
      formatter: (p: { name?: string; data?: { source?: string; target?: string; value?: number } }) => {
        if (p.data?.source && p.data?.target) {
          return `${p.data.source} → ${p.data.target}<br/><b>${humanizeMeasure(valueKey)}:</b> ${formatValue(p.data.value, valueFmt)}`;
        }
        return p.name ?? "";
      },
    },
    series: [
      {
        type: "sankey",
        data: Array.from(nodeSet).map((name, i) => ({
          name,
          itemStyle: { color: palette[i % palette.length] },
        })),
        links,
        emphasis: { focus: "adjacency" },
        // Solid color flows tied to the source node are easier to read
        // than gradient flows when N flows > ~8 — gradients turn the
        // chart into a tie-dye blur.
        lineStyle: { color: "source", curveness: 0.5, opacity: 0.5 },
        // Default ECharts sankey labels render to the RIGHT of every
        // node, which pushes target-column labels off-canvas (you see
        // empty space on the right of the chart). Use per-level
        // overrides so source labels render right (inside chart) and
        // target labels render left (also inside chart).
        levels: [
          { depth: 0, label: { position: "right" } },
          { depth: 1, label: { position: "left" } },
        ],
        label: {
          color: "#111",
          fontSize: 11,
          // Strip a leading "Cube.field-" prefix that occasionally
          // leaks through as a node name when the dim's underlying
          // values carry the field path. Then truncate long labels
          // so they don't bleed into adjacent columns. Tooltip shows
          // the full original name.
          formatter: (p: { name: string }) => {
            const cleaned = p.name.replace(/^[A-Z][A-Za-z]+\.[a-zA-Z]+[-_]/, "");
            return cleaned.length > 22 ? cleaned.slice(0, 21) + "…" : cleaned;
          },
        },
        // "left" instead of "justify" leaves room on the right for the
        // target column's left-positioned labels.
        nodeAlign: "left",
        nodeGap: 6,
        nodeWidth: 12,
      },
    ],
  } as ChartOption;
}
